// Constantes normativas del Plan de Emergencias v2, cada una con su fuente.
// RD 164/2025 (referencia técnica, PE-14), AM 1257 (nacional) y RTQ 2026 (Quito).
// Filas de la matriz: docs/normativa/plan_emergencias/MATRIZ_NORMATIVA.md.

import { doc, unirFuentes, ValorConFuente } from './pe-tipos';

// ── RD 164/2025, Anexo I ─────────────────────────────────────────────────────

// Tabla 1.3.2: Ci por grado de peligrosidad (1,00 también para el grado 5).
export const CI_RD164 = {
  normales: [1.0, 1.2, 1.4, 1.6],
  fumigenos: [1.2, 1.44, 1.68, 1.92],
  norma: 'RD 164/2025',
  articulo_o_tabla: 'Anexo I, Tabla 1.3.2',
  fuente_doc: doc.rd164(6203),
  estado: 'transcrito_de_imagen' as const,
};

// Tabla 1.3.4: coeficiente R de la actividad.
export const R_RD164 = {
  valores: [0.8, 1, 1.4, 1.8],
  norma: 'RD 164/2025',
  articulo_o_tabla: 'Anexo I, Tabla 1.3.4',
  fuente_doc: doc.rd164('6211-6214'),
  estado: 'transcrito_de_imagen' as const,
};

// Fórmula 3.2.1: Qs = [Σ(qi·Gi·Ci) / A] · R.
export const FORMULA_QS_RD164 = {
  norma: 'RD 164/2025',
  articulo_o_tabla: 'Anexo I, 3.2.1',
  fuente_doc: doc.rd164(6185),
};

// Tabla 1.3.1: nivel de riesgo intrínseco por Qs (MJ/m²). "hasta" es el límite superior incluido.
export const NIVELES_RD164: { nivel: number; categoria: 'bajo' | 'medio' | 'alto'; hasta: number; linea: number }[] = [
  { nivel: 1, categoria: 'bajo', hasta: 425, linea: 2846 },
  { nivel: 2, categoria: 'bajo', hasta: 850, linea: 2848 },
  { nivel: 3, categoria: 'medio', hasta: 1275, linea: 2849 },
  { nivel: 4, categoria: 'medio', hasta: 1700, linea: 2850 },
  { nivel: 5, categoria: 'medio', hasta: 3400, linea: 2851 },
  { nivel: 6, categoria: 'alto', hasta: 6800, linea: 2852 },
  { nivel: 7, categoria: 'alto', hasta: 13600, linea: 2853 },
  { nivel: 8, categoria: 'alto', hasta: Infinity, linea: 2854 },
];

// Tabla 1.3.3, fila "Papel." (ejemplo de §3.3.8 del PASO0; verificada contra el BOE, pág. 55).
export const PAPEL_RD164 = {
  qiMJkg: 16.5,
  ci: 1.2,
  ciCompactado: 1.0, // nota 6: papel o cartón compactado
  fuente_doc: doc.rd164(3357),
  fuente_nota6: doc.rd164('3464-3465'),
};

// ── AM 1257 ──────────────────────────────────────────────────────────────────

// Art. 139: "Menos de 160,000" (leve), "Entre 160,000 y 340,000" (ordinario), "Más de 340,000" (extra).
// Por el texto, 160 000 y 340 000 son ordinario.
export const ESCALA_ART139 = {
  leveMenorQue: 160000,
  extraMayorQue: 340000,
  unidad: 'kcal/m²',
  norma: 'AM 1257',
  articulo_o_tabla: 'Art. 139',
  fuente: { leve: doc.am1257(2285), ordinario: doc.am1257(2301), extra: doc.am1257(2315) },
  fuente_matriz: doc.matriz(20),
};

export const EXTINTOR_DISTANCIA_NACIONAL: ValorConFuente<number> = {
  valor: 20, norma: 'AM 1257', articulo_o_tabla: 'Art. 120',
  fuente_doc: unirFuentes(doc.am1257('2015-2026'), doc.matriz(100)), estado: 'verificado',
};

// Recorrido máximo de evacuación (PE-A3c2, caso T10). Matriz 2.1, 2.9 y 2.11 (verificados).
// Nacional: AM 1257 Art. 9, 25 m, "sin embargo, puede variar en función del tipo de edificación y grado de riesgo".
export const RECORRIDO_NACIONAL: ValorConFuente<number> = {
  valor: 25, norma: 'AM 1257', articulo_o_tabla: 'Art. 9',
  fuente_doc: unirFuentes(doc.am1257('314-318'), doc.matriz(39)), estado: 'verificado',
};

// Niveles de riesgo de los contenidos: los tres que usa la RTQ 3 (6.3 b y 11.2 b), definidos en la RTQ 1.
export type NivelRiesgoRtq3 = 'bajo' | 'ordinario' | 'alto';
export const NIVELES_RIESGO_RTQ3: { valor: NivelRiesgoRtq3; etiqueta: string; fuente_doc: string }[] = [
  { valor: 'bajo', etiqueta: 'Riesgo ligero, bajo o leve', fuente_doc: doc.rtq(1, 718) },
  { valor: 'ordinario', etiqueta: 'Riesgo ordinario o moderado', fuente_doc: doc.rtq(1, 731) },
  { valor: 'alto', etiqueta: 'Riesgo alto o extra', fuente_doc: doc.rtq(1, 746) },
];

// RTQ 3/2026, en metros: [sin rociadores, con rociadores automáticos]. En industrial de riesgo alto la RTQ no da
// un valor con rociadores: se usa el mismo 25 m (no se amplía sin texto que lo diga).
export const RECORRIDO_RTQ3: Record<'almacenamiento' | 'industrial', {
  articulo: string; fuente_doc: string; limites: Record<NivelRiesgoRtq3, [number, number]>;
}> = {
  almacenamiento: { articulo: '6.3 (b)', fuente_doc: unirFuentes(doc.rtq(3, '602-635'), doc.matriz(47)),
    limites: { bajo: [120, 120], ordinario: [60, 120], alto: [25, 30] } },
  industrial: { articulo: '11.2 (b)', fuente_doc: unirFuentes(doc.rtq(3, '1394-1409'), doc.matriz(49)),
    limites: { bajo: [45, 60], ordinario: [45, 60], alto: [25, 25] } },
};

export const BRIGADA_NACIONAL: ValorConFuente<number> = {
  valor: 20, norma: 'AM 1257', articulo_o_tabla: 'Art. 169',
  fuente_doc: unirFuentes(doc.am1257('2775-2779'), doc.matriz(133)), estado: 'verificado',
};

// Art. 23; valores que usa el motor por PE-7 (texto ambiguo en el nivel final).
export const ILUMINACION_NACIONAL = {
  duracionMin: 60,
  luxPromedio: 10,
  luxMinimo: 1,
  norma: 'AM 1257',
  articulo_o_tabla: 'Art. 23',
  fuente_doc: unirFuentes(doc.am1257('512-520'), doc.matriz('74-75')),
};

// Art. 32 d): "prueba hidrostática cada seis (6) años". El plan de referencia dice "a partir del
// quinto año": por defecto 6 años, editable, mostrando "AM 1257, Art. 32 d)" (PE-32, Kevin 26/10/2026).
export const HIDROSTATICA_ANOS: ValorConFuente<number> = {
  valor: 6, norma: 'AM 1257', articulo_o_tabla: 'Art. 32 d)',
  fuente_doc: unirFuentes(doc.am1257(669), doc.matriz(92)), estado: 'verificado', pe: 'PE-32',
};

// DE 255 Anexo 1, PO-16: el plan es siempre obligatorio (PE-4).
export const PLAN_OBLIGATORIO = {
  norma: 'DE 255', articulo_o_tabla: 'Anexo 1, PO-16', fuente_doc: doc.matriz(143),
};

// ── RTQ 2026 (Quito) ─────────────────────────────────────────────────────────

export const EXTINTOR_DISTANCIA_QUITO: ValorConFuente<number> = {
  valor: 15, norma: 'RTQ 7/2026', articulo_o_tabla: '15.4',
  fuente_doc: unirFuentes(doc.rtq(7, '384-385'), doc.matriz(88)), estado: 'verificado',
};

export const BRIGADA_QUITO_INDUSTRIAL: ValorConFuente<number> = {
  valor: 25, norma: 'RTQ 3/2026', articulo_o_tabla: '11.5 (i)',
  fuente_doc: unirFuentes(doc.rtq(3, '1547-1549'), doc.matriz(126)), estado: 'verificado',
};

export const ILUMINACION_QUITO = {
  luxSuelo: 10,
  separacionMaxM: 12,
  norma: 'RTQ 5/2026',
  articulo_o_tabla: '9.1 y 9.2.1',
  fuente_doc: unirFuentes(doc.rtq(5, '582-592'), doc.matriz(80)),
};

export type OcupacionFCA =
  | 'industrial' | 'oficinas' | 'almacenamiento' | 'residencial' | 'alojamiento'
  | 'aulas' | 'talleres_laboratorios' | 'guarderias' | 'salud_tratamiento'
  | 'mercantil_planta_baja' | 'mercantil_sobre_planta_baja';

// RTQ 5, Tabla 1 (m² por persona, área bruta). Almacenamiento: N/A en la tabla.
export const FCA_RTQ5: Record<OcupacionFCA, { fca: number | null; linea: number }> = {
  almacenamiento: { fca: null, linea: 137 },
  aulas: { fca: 1.9, linea: 140 },
  talleres_laboratorios: { fca: 4.6, linea: 141 },
  guarderias: { fca: 3.3, linea: 142 },
  residencial: { fca: 11, linea: 143 },
  industrial: { fca: 9.3, linea: 144 },
  mercantil_planta_baja: { fca: 2.8, linea: 147 },
  mercantil_sobre_planta_baja: { fca: 5.6, linea: 150 },
  oficinas: { fca: 9.3, linea: 152 },
  salud_tratamiento: { fca: 22.3, linea: 202 },
  alojamiento: { fca: 11, linea: 205 },
};

// RTQ 5, Tabla 2: ancho por persona (mm).
export const FC_RTQ5 = {
  general: { escaleras: 7.6, puertas: 5 },
  alto_riesgo: { escaleras: 18, puertas: 10 },
  norma: 'RTQ 5/2026',
  articulo_o_tabla: '5.2 y Tabla 2',
  fuente_doc: unirFuentes(doc.rtq(5, '225-247'), doc.matriz(62)),
};

// RTQ 5, Tablas 3 y 4 (5.3, pág. 9; verificadas contra el PDF, PE-36): puertas 0,86 m en las dos.
export const PUERTA_MINIMA_M = { valor: 0.86, fuente_doc: unirFuentes(doc.rtq(5, '306-323'), doc.matriz(63)) };

// RTQ 5, Tablas 3 (nuevas) y 4 (existentes): ancho mínimo de escaleras según el aforo. Los cortes van tal como
// están en cada tabla: en la 3, "igual o superior a 50" / "inferior a 50"; en la 4, "menor o igual a 50" /
// "superior a 50 y menor a 120" / "igual o superior a 120".
export const ESCALERA_MINIMA_RTQ5 = {
  nueva: { tabla: 'Tabla 3', fuente_doc: unirFuentes(doc.rtq(5, '308-314'), doc.matriz(63)),
    minimo: (aforo: number) => (aforo >= 50 ? 1.2 : 0.9),
    corte: (aforo: number) => (aforo >= 50 ? 'aforo igual o superior a 50 personas' : 'aforo inferior a 50 personas') },
  existente: { tabla: 'Tabla 4', fuente_doc: unirFuentes(doc.rtq(5, '316-323'), doc.matriz(63)),
    minimo: (aforo: number) => (aforo <= 50 ? 0.7 : aforo < 120 ? 0.9 : 1.2),
    corte: (aforo: number) => (aforo <= 50 ? 'aforo menor o igual a 50 personas'
      : aforo < 120 ? 'aforo superior a 50 personas y menor a 120 personas' : 'aforo igual o superior a 120 personas') },
  // 5.2.2: con más de un piso, la capacidad de cada piso se calcula solo con sus ocupantes.
  pisos_fuente_doc: doc.rtq(5, '248-251'),
};
